import { normalizeLocaleName } from '../../../../../../../../scripts/deploy/locale';
import { CoreStore } from '../../../../core/store/core.store';
import { MobileSignInSessionListComponent } from '../../../../shared/modules/mobile-sign-in/components/mobile-sign-in-session-list/mobile-sign-in-session-list.component';
import { MobileSignInService } from '../../../../shared/modules/mobile-sign-in/services/mobile-sign-in.service';
import { createUnlockRouteState } from '../../../../shared/modules/two-factor/components/two-factor-unlock/two-factor-unlock.component';
import {
  filterMethodsByTypes,
  findMethodByType,
  methodTypeToIconMap,
  TWO_FACTOR_SETUP_METHODS_BY_PRIORITY,
  TwoFAType
} from '../../../../shared/modules/two-factor/domain/two-factor';
import { TwoFactorSetupService } from '../../../../shared/modules/two-factor/services/two-factor-setup.service';
import { TwoFactorService, TwoFAResult } from '../../../../shared/modules/two-factor/services/two-factor.service';
import { isMobileSignInEnabled } from '../../../login/store/login.selectors';
import { UserProfileService } from '../../services/user-profile.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getAvailableIdentityProviders,
  getCurrentIdpId,
  getLoggedInUser,
  getLoggedInUserProfile,
  getLoggedInUserProfilePictureUrl,
  LocaleService
} from '@ppl/auth';
import { getAppAbsoluteURL } from '@ppl/core';
import { ViewLoadState } from '@ppl/domain';
import { getLocalesAsOptions, I18nService } from '@ppl/i18n';
import { AsyncState } from '@ppl/store';
import { ButtonType } from '@ppl/ui/button';
import { PplDialogService } from '@ppl/ui/dialog';
import { TFormBuilder } from '@ppl/ui/form';
import { wrapAPICall } from '@ppl/utils/api.utils';
import { sortByKey } from '@ppl/utils/array.utils';
import { DialogResult } from '@ppl/utils/dialog.utils';
import { markAllControlsAsDirty } from '@ppl/utils/form.utils';
import { LANGUAGES } from '@ppl/utils/language.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import { getTimezoneIANAOptions, TIMEZONE_AUTO_DETECT_VALUE } from '@ppl/utils/timezone.utils';
import { assertNever } from '@ppl/utils/typescript.utils';
import {
  BehaviorSubject,
  EMPTY,
  forkJoin,
  NEVER,
  of,
  Subject
} from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';
import type {
  UntypedFormGroup
} from '@angular/forms';
import type {
  AuthUser
} from '@ppl/auth';
import type { DateTime, UpdateUserProfileInput, UpdateUserSettingInput } from '@ppl/graphql-user-api';
import type { AsyncValue } from '@ppl/store';
import type { TFormGroup } from '@ppl/ui/form';
import type { PplSelectOption } from '@ppl/ui/select';
import type {
  Observable,
  Subscription
} from 'rxjs';
import type { ItemsNavItem } from '../../../../shared/components/items-nav/items-nav.component';
import type { TwoFactorBackupCodesToggleEvent } from '../../../../shared/modules/two-factor/components/two-factor-backup-codes/two-factor-backup-codes.component';
import type { TwoFactorMethodEntry } from '../../../../shared/modules/two-factor/components/two-factor-method/two-factor-method.component';
import type { TwoFactorUnlockNavigateBackRouteState } from '../../../../shared/modules/two-factor/components/two-factor-unlock/two-factor-unlock.component';
import type {
  TwoFactorSecurityTokenData,
  TwoFactorSetupMethodType,
  TwoFAMethod
} from '../../../../shared/modules/two-factor/domain/two-factor';

@Component({
  selector: 'ppl-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UserProfileComponent implements OnInit, OnDestroy {

  @ViewChild('mobileSignInSessionList') mobileSignInSessionList: MobileSignInSessionListComponent;

  loading$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);

  form: UntypedFormGroup;
  defaultIDPForm: TFormGroup<DefaultIDPFormData>;

  loggedInUser: AuthUser = this.store.get(getLoggedInUser);

  localesLoading$ = new Subject();
  pictureUrl: string;
  languages = LANGUAGES;
  availableIDPs: PplSelectOption[] = [];
  timezones = getTimezoneIANAOptions(this.i18nService, this.loggedInUser.profile);
  regions = [];
  currentDateTime = new Date().toISOString();
  mobileSignInEnabled: boolean;

  lastPasswordChange$ = new BehaviorSubject<AsyncValue<DateTime>>({ state: AsyncState.IDLE });
  lastPasswordChangeLoadState$ = this.lastPasswordChange$.pipe(
    map(value => {
      switch (value.state) {
        case AsyncState.FETCHING:
          return ViewLoadState.FETCHING;
        case AsyncState.FETCHED:
          return ViewLoadState.FETCHED;
        case AsyncState.ERROR:
          return ViewLoadState.ERROR;
        case AsyncState.IDLE:
          return null;
        default:
          assertNever(value.state);
      }
    })
  );

  securityTokenData$ = new BehaviorSubject<TwoFactorSecurityTokenData>(null);
  securityToken$ = this.securityTokenData$.pipe(
    map(data => {
      return this.getSecurityToken(data);
    })
  );

  tabItem$ = new BehaviorSubject<TabItem>(TabItem.Personal);
  tabItems: ItemsNavItem[] = [
    { value: TabItem.Personal, label: this.i18nService.translate('Personal'), icon: 'personal-info-tab-white' },
    { value: TabItem.Security, label: this.i18nService.translate('Security'), icon: 'security-info-tab-white' }
  ];

  twoFactorMethods$ = new BehaviorSubject<TwoFAMethod[]>([]);
  // Backend can return also two factor methods that are not enabled.
  twoFactorMethodsEnabled$ = this.twoFactorMethods$.pipe(map(methods => this.getEnabledTwoFAMethods(methods)));
  twoFactorEnabled$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      return Boolean(
        filterMethodsByTypes(
          methods,
          [TwoFAType.AUTH_APP, TwoFAType.EMAIL, TwoFAType.SMS]).length
      );
    })
  );
  twoFactorEnabledSince$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      const relevantMethods = filterMethodsByTypes(
        methods,
        [TwoFAType.AUTH_APP, TwoFAType.EMAIL, TwoFAType.SMS]
      ).sort(sortByKey('created'));
      return relevantMethods.length
        ? relevantMethods[0].created
        : null;
    })
  );
  twoFactorEmails$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      return filterMethodsByTypes(methods, [TwoFAType.EMAIL]).map(method => {
        return this.createTwoFactorMethodEntry(method);
      });
    })
  );
  twoFactorSMS$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      return filterMethodsByTypes(methods, [TwoFAType.SMS]).map(method => {
        return this.createTwoFactorMethodEntry(method);
      });
    })
  );
  twoFactorAuthApps$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      return filterMethodsByTypes(methods, [TwoFAType.AUTH_APP]).map(method => {
        return this.createTwoFactorMethodEntry(method);
      });
    })
  );
  twoFactorBackupCodes$ = this.twoFactorMethodsEnabled$.pipe(
    map(methods => {
      return findMethodByType(methods, TwoFAType.BACKUP_CODES);
    })
  );

  fetchSessionsInterval: any = null;
  fetchSessionsCount = 0;

  methodTypeToIconMap = methodTypeToIconMap;
  TabItem = TabItem;
  TwoFAType = TwoFAType;
  MAX_TWO_FACTOR_METHOD_COUNT = MAX_TWO_FACTOR_METHOD_COUNT;

  private subscriptions: Subscription[] = [];

  get securityTokenData() {
    return this.securityTokenData$.value;
  }

  get securityToken() {
    return this.getSecurityToken(this.securityTokenData);
  }

  get twoFactorMethodsEnabled() {
    return this.getEnabledTwoFAMethods(this.twoFactorMethods$.value);
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private tfb: TFormBuilder,
    private i18nService: I18nService,
    private store: CoreStore,
    private userProfileService: UserProfileService,
    private dialogService: PplDialogService,
    private localeService: LocaleService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private twoFactorService: TwoFactorService,
    private twoFactorSetupService: TwoFactorSetupService,
    private mobileSignInService: MobileSignInService
  ) {
    const user = this.loggedInUser;
    this.form = this.createForm(user);
    this.defaultIDPForm = this.tfb.group<DefaultIDPFormData>({
      defaultIdp: [
        user.profile.defaultIdp
          ? user.profile.defaultIdp.id
          : PIPELINER_IDP_TEMP_ID,
        Validators.required
      ]
    });
    this.pictureUrl = this.store.get(getLoggedInUserProfilePictureUrl);
    this.mobileSignInEnabled = this.store.get(isMobileSignInEnabled);

    const navigation = this.router.getCurrentNavigation();
    const unlockBackState = navigation?.extras?.state as TwoFactorUnlockNavigateBackRouteState<UserProfileUnlockNavigateBackState>;
    if (unlockBackState) {
      const { security, navigateBackState } = unlockBackState.unlockData;
      this.securityTokenData$.next(security);
      if (navigateBackState) {
        this.tabItem$.next(navigateBackState.tab);
      }
    }
  }

  pageshowEventHandler = (event: PageTransitionEvent) => {
    // NOTE(mike): If restoring from `bfcache`.
    if (event.persisted) {
      this.saving$.next(false);
      // NOTE(mike): When developing locally this will always reload,
      // because `$localize.locale` is `en`, which is always different
      // from the language code set in user settings.
      if (normalizeLocaleName($localize.locale) !== this.store.get(getLoggedInUser).settings.language) {
        window.location.reload();
      }
    }
  };

  ngOnInit() {
    window.addEventListener('pageshow', this.pageshowEventHandler);
    this.initializeLocales();
    this.initializeIdentityProviders();
    this.initLastPasswordChange();
    this.subscriptions.push(
      this.tabItem$.pipe(
        switchMap(tabItem => {
          return tabItem === TabItem.Security
            ? this.callTwoFactorAPI(this.fetchMethods(), this.loading$)
            : EMPTY;
        })
      ).subscribe()
    );
    this.subscriptions.push(
      this.twoFactorService.securityTokenExpired$.subscribe(data => {
        if (data.securityToken === this.securityToken) {
          this.securityTokenData$.next(null);
          this.showSecuritySessionExpiredToast();
        }
      }),
      this.mobileSignInService.securityTokenExpired$.subscribe(data => {
        if (data.securityToken === this.securityToken) {
          this.securityTokenData$.next(null);
          this.showSecuritySessionExpiredToast();
        }
      })
    );
  }

  ngOnDestroy() {
    window.removeEventListener('pageshow', this.pageshowEventHandler);
    this.subscriptions.forEach(unsubscribe);
    clearInterval(this.fetchSessionsInterval);
  }

  initializeLocales() {
    this.regions = getLocalesAsOptions();
  }

  initializeIdentityProviders() {
    const primaryIdp = this.store.get(getLoggedInUserProfile).defaultIdp;
    const availableIDPs = this.store.get(getAvailableIdentityProviders);
    const availableIDPsAsOptions: PplSelectOption[] = availableIDPs.map(item => ({
      value: item['id'],
      label: item['name'],
      disabled: !item.enabled
    }));
    const idpOptions: PplSelectOption[] = [PIPELINER_IDP, ...availableIDPsAsOptions];

    if (primaryIdp && !availableIDPs.find(idp => idp.id === primaryIdp.id)) {
      idpOptions.push({ value: primaryIdp.id, label: primaryIdp.name, deleted: true });
      setTimeout(() => {
        this.defaultIDPForm.get('defaultIdp').setErrors({ 'custom': this.i18nService.translate('Selected IDP is no longer available') });
        this.defaultIDPForm.get('defaultIdp').markAsTouched();
        this.changeDetectorRef.detectChanges();
      });

    }
    this.availableIDPs = idpOptions;
  }

  initLastPasswordChange() {
    this.subscriptions.push(this.fetchLastPasswordChange().subscribe());
  }

  createForm(user: AuthUser) {
    const timezoneValue = user.profile.timezoneAutodetect
      ? TIMEZONE_AUTO_DETECT_VALUE
      : user.profile.timezoneName || '';
    const controlsConfig = {
      email: new UntypedFormControl(
        { value: user.profile.email, disabled: true },
        [Validators.required]
      ),
      firstName: [user.profile.firstName, Validators.required],
      middleName: [user.profile.middleName],
      lastName: [user.profile.lastName, Validators.required],
      // TODO(mike): Picture.
      company: [user.profile.company],
      telephone: [user.profile.telephone],
      locale: [user.settings.locale],
      language: [user.settings.language],
      timezone: [timezoneValue, Validators.required]
    };

    const form = this.fb.group(controlsConfig);
    this.subscriptions.push(form.get('locale').valueChanges.pipe(
      switchMap((locale) => {
        this.localesLoading$.next(true);
        this.changeDetectorRef.detectChanges();
        return this.localeService.registerLocale(locale);
      }),
      tap(() => {
        this.localesLoading$.next(false);
        this.changeDetectorRef.detectChanges();
      })
    ).subscribe());
    return form;
  }

  createTwoFactorMethodEntry(method: TwoFAMethod): TwoFactorMethodEntry {
    switch (method.type) {
      case TwoFAType.AUTH_APP:
        return {
          id: String(method.id),
          value: method.display_name,
          created: method.created
        };
      case TwoFAType.EMAIL:
      case TwoFAType.SMS:
        return {
          id: String(method.id),
          value: method.value[0],
          created: method.created
        };
    }
  }

  onPictureChange(event: string) {
    if (event === '') { // empty string means picture was removed
      this.userProfileService.removeProfilePicture();
    } else if (event) { // truthy string goes right into mutation
      this.userProfileService.setProfilePicture(event);
    }
  }

  onTabItemClick(value: TabItem) {
    if (value === TabItem.Security && this.twoFactorService.needSecurityTokenRefresh(this.securityTokenData)) {
      this.requestSecurityToken();
    } else {
      this.tabItem$.next(value);
    }
  }

  onDefaultIDPChange(value: string) {
    const currentIdp = this.defaultIDPForm.value.defaultIdp;
    const nextIdpOption = this.availableIDPs.find(idp => idp.value === value);
    const nextIdp = nextIdpOption.value;

    if (currentIdp !== nextIdp) {
      const description = this.i18nService.translate(
        'UserProfileChangeIDPDescription',
        { idp_name: nextIdpOption.label }
      );
      this.dialogService.confirm({
        text: this.i18nService.translate('Changing_the_Primary_IDP'),
        description,
        actionLabel: this.i18nService.translate('I_am_aware,_Continue')
      }).pipe(
        switchMap(shouldChange => {
          if (shouldChange) {
            const profileInput: UpdateUserProfileInput = {
              id: this.loggedInUser.profile.id,
              defaultIdpId: !nextIdp || nextIdp === PIPELINER_IDP_TEMP_ID ? null : nextIdp as unknown as number,
            };
            return wrapAPICall(
              this.userProfileService.updateProfile(profileInput),
              this.saving$
            ).pipe(
              tap(() => {
                this.defaultIDPForm.setValue({ defaultIdp: nextIdp });
              })
            );
          } else {
            return EMPTY;
          }
        })
      ).subscribe();
    }
  }

  onSave() {
    if (!this.form.valid) {
      markAllControlsAsDirty(this.form);
      return;
    }
    const values = this.form.getRawValue();
    const storeUser: AuthUser = this.store.get(getLoggedInUser);
    const locale = values.locale;
    // inputs
    const settingsInput: UpdateUserSettingInput = {
      locale,
      language: values.language
    };
    const profileInput: UpdateUserProfileInput = {
      id: storeUser.profile.id,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      middleName: values.middleName,
      company: values.company,
      telephone: values.telephone,
      ...values.timezone === TIMEZONE_AUTO_DETECT_VALUE
        ? { timezoneAutodetect: true }
        : { timezoneAutodetect: false, timezoneName: values.timezone }
    };

    this.saving$.next(true);

    // TODO: create this as one graphql request with three mutations and handle errors
    this.subscriptions.push(
      forkJoin([
        this.userProfileService.updatePreferences(settingsInput),
        this.userProfileService.updateProfile(profileInput)
      ]).subscribe(() => {
        this.navigateToDashboard();
      })
    );
  }

  onCancel() {
    this.navigateToDashboard();
  }

  onChangePassword() {
    const dialogRef = this.userProfileService.changeProfilePassword();
    dialogRef.afterClosed().subscribe(response => {
      if (response && response.result === DialogResult.SUCCESS) {
        this.navigateToDashboard();
      }
    });
  }

  onShowQrCodeClick() {
    clearInterval(this.fetchSessionsInterval);
    this.fetchSessionsCount = 0;

    this.mobileSignInService.openQrCodeDialog({
      securityToken: this.securityToken
    }).subscribe(() => {
      // Try to refetch sessions list three times after closing the qr dialog
      this.fetchSessionsInterval = setInterval(() => {
        if (this.fetchSessionsCount < 3) {
          this.fetchSessionsCount++;
          this.mobileSignInSessionList?.fetchSessions();
        } else {
          clearInterval(this.fetchSessionsInterval);
        }
      }, 2000);

      this.fetchSessionsCount++;
      this.mobileSignInSessionList?.fetchSessions();
    });
  }

  onToggle2StepVerification(enable: boolean) {
    if (enable) {
      this.addTwoFactorMethod(TWO_FACTOR_SETUP_METHODS_BY_PRIORITY[0]).pipe(
        switchMap(methods => {
          const backupCodes = findMethodByType(this.twoFactorMethodsEnabled, TwoFAType.BACKUP_CODES);
          return backupCodes
            ? this.dialogService.info({
              text: this.i18nService.translate('TwoFactorBackupCodesGeneratedInfoMessage')
            }).pipe(
              map(() => {
                return methods;
              })
            )
            : of(methods);
        })
      ).subscribe();
    } else {
      this.dialogService.confirm({
        text: this.i18nService.translate('Turn_off_2-Step_Verification?'),
        description: this.i18nService.translate('UserProfileToggleTwoFactorsConfirmDescription'),
        actionLabel: this.i18nService.translate('Turn_Off'),
        actionButtonType: ButtonType.Button5
      }).pipe(
        switchMap(turnOff => {
          if (turnOff) {
            return this.callTwoFactorAPI(
              this.twoFactorService.disable2StepVerification({ securityToken: this.securityToken }).pipe(
                switchMap(() => {
                  return this.fetchMethods();
                })
              ),
              this.loading$
            );
          } else {
            return EMPTY;
          }
        })
      ).subscribe();
    }
  }

  onToggleBackupCodes(event: TwoFactorBackupCodesToggleEvent) {
    this.toggleBackupCodes(event).subscribe();
  }

  onTwoFactorAdd(type: TwoFAType.AUTH_APP | TwoFAType.EMAIL | TwoFAType.SMS) {
    this.addTwoFactorMethod(type).subscribe();
  }

  onTwoFactorDelete(id: number) {
    this.dialogService.confirm({
      text: this.i18nService.translate('Are_you_sure_you_want_to_delete_this_method?')
    }).pipe(
      switchMap(yes => {
        if (yes) {
          return this.callTwoFactorAPI(
            this.twoFactorService.deleteMethod({ id, securityToken: this.securityToken }).pipe(
              switchMap(() => {
                return this.fetchMethods();
              })
            ),
            this.loading$
          );
        } else {
          return EMPTY;
        }
      })
    ).subscribe();
  }

  fetchLastPasswordChange() {
    this.lastPasswordChange$.next({ state: AsyncState.FETCHING });
    return this.userProfileService.fetchLastPasswordChange().pipe(
      map(lastPasswordChange => {
        return {
          state: AsyncState.FETCHED,
          value: lastPasswordChange
        };
      }),
      tap(value => {
        this.lastPasswordChange$.next(value);
      }),
      catchError(error => {
        const value = {
          state: AsyncState.ERROR,
          error
        };
        this.lastPasswordChange$.next(value);
        return of(value);
      })
    );
  }

  fetchMethods() {
    return this.twoFactorService.fetchMethods().pipe(
      this.twoFactorService.handleError({
        errorHandlerFn: (error, originalError) => {
          this.dialogService.alert({
            text: this.i18nService.translate('Failed_to_fetch_2-Step_Verification_configuration.'),
            actionLabel: this.i18nService.translate('Ok')
          });
          return NEVER;
        }
      }),
      tap(methods => {
        this.twoFactorMethods$.next(methods);
      })
    );
  }

  addTwoFactorMethod(type: TwoFactorSetupMethodType) {
    return this.twoFactorSetupService.setupMethod({
      type,
      email: this.loggedInUser.profile.email,
      securityToken: this.securityToken
    }).pipe(
      switchMap(response => {
        return response && response.result === DialogResult.SUCCESS
          ? this.fetchMethods()
          : EMPTY;
      })
    );
  }

  toggleBackupCodes(event: TwoFactorBackupCodesToggleEvent) {
    return this.callTwoFactorAPI(
      this.twoFactorService.toggleBackupCodes({ ...event, securityToken: this.securityToken }).pipe(
        switchMap(response => {
          return response.result !== TwoFAResult.FINISHED
            ? EMPTY
            : this.fetchMethods();
        })
      ),
      this.loading$
    );
  }

  private showSecuritySessionExpiredToast() {
    this.twoFactorService.showExpiredTokenToast(
      'user-profile-security-token-expiry-toast',
      {
        message: this.i18nService.translate('Your_security_session_expired.'),
        refresh: () => {
          if (this.twoFactorService.needSecurityTokenRefresh(this.securityTokenData)) {
            this.dialogService.closeAll();
            this.requestSecurityToken();
          }
        }
      }
    );
  }

  private requestSecurityToken() {
    const unlockState = createUnlockRouteState<UserProfileUnlockNavigateBackState>({
      idpId: this.store.get(getCurrentIdpId),
      email: this.loggedInUser.profile.email,
      navigateBackUrl: this.router.url,
      navigateBackState: {
        tab: TabItem.Security
      }
    });
    this.router.navigate(
      ['../unlock'],
      { relativeTo: this.activatedRoute, state: unlockState }
    );
  }

  private getSecurityToken(data: TwoFactorSecurityTokenData | null) {
    return data ? data.token : null;
  }

  private callTwoFactorAPI(observable$: Observable<any>, progress$: BehaviorSubject<boolean>) {
    return wrapAPICall(observable$, progress$);
  }

  private navigateToDashboard() {
    window.location.href = getAppAbsoluteURL('/');
  }

  private getEnabledTwoFAMethods(methods: TwoFAMethod[]): TwoFAMethod[] {
    return methods.filter(it => it.enabled);
  }
}

interface DefaultIDPFormData {
  defaultIdp: string;
}

const MAX_TWO_FACTOR_METHOD_COUNT = 2;
const PIPELINER_IDP_TEMP_ID = 'PIPELINER_IDP';
const PIPELINER_IDP: PplSelectOption = { value: PIPELINER_IDP_TEMP_ID, label: 'Pipeliner IDP' };

enum TabItem {
  Personal = 'Personal',
  Security = 'Security'
}

interface UserProfileUnlockNavigateBackState {
  tab: TabItem;
}
